function getApiBase() {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined') {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:8000/api';
    }
    // Works across all Apache/LiteSpeed/cPanel configurations without requiring URL rewrite rules
    return 'https://api.zimetafsiriwa.site/index.php/api';
  }
  return 'https://api.zimetafsiriwa.site/index.php/api';
}

const API_BASE = getApiBase();

let accessToken = null;
let refreshTokenPromise = null;
let sessionExpired = false;
let sessionVersion = 0;
let sessionExpiredHandler = null;

export function onSessionExpired(handler) {
  sessionExpiredHandler = handler;
  return () => { if (sessionExpiredHandler === handler) sessionExpiredHandler = null; };
}

export function clearApiSession() {
  accessToken = null;
  refreshTokenPromise = null;
  sessionVersion += 1;
  clearApiCache();
}

function sessionExpiredError() {
  const error = new Error('Your session has expired. Please sign in again.');
  error.status = 401;
  error.code = 'SESSION_EXPIRED';
  return error;
}

async function expireSession(version = sessionVersion) {
  if (version === sessionVersion && !sessionExpired) {
    sessionExpired = true;
    clearApiSession();
    await sessionExpiredHandler?.();
  }
  throw sessionExpiredError();
}

// Lightweight in-memory cache for ultra-fast page transitions
const memoryCache = new Map();
const CACHE_TTL_MS = 20000; // 20s TTL for static catalogue lookups

export function clearApiCache() {
  memoryCache.clear();
}

async function getFirebaseIdToken(version = sessionVersion) {
  const { auth } = await import('./firebase');
  const user = auth.currentUser;
  if (!user) return expireSession(version);
  try {
    return await user.getIdToken();
  } catch (error) {
    if (['auth/id-token-expired', 'auth/user-token-expired', 'auth/invalid-user-token', 'auth/user-disabled', 'auth/user-not-found'].includes(error.code)) {
      return expireSession(version);
    }
    throw error;
  }
}

async function refreshAccessToken() {
  if (sessionExpired) throw sessionExpiredError();
  if (refreshTokenPromise) return refreshTokenPromise;
  const version = sessionVersion;
  const refresh = (async () => {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (version !== sessionVersion) throw sessionExpiredError();
    if (res.status === 401) return expireSession(version);
    if (!res.ok) throw new Error(`Unable to refresh your session. Please try again (${res.status}).`);
    const token = res.headers.get('X-Access-Token');
    if (!token) throw new Error('Unable to renew your session. Please try again.');
    accessToken = token;
    return accessToken;
  })();
  refreshTokenPromise = refresh;
  try {
    return await refresh;
  } finally {
    if (refreshTokenPromise === refresh) refreshTokenPromise = null;
  }
}

async function request(endpoint, options = {}, useCache = false) {
  const method = (options.method || 'GET').toUpperCase();
  const cacheKey = `${method}:${endpoint}`;
  const version = sessionVersion;

  // Instant response from memory cache if available and not expired
  if (useCache && method === 'GET') {
    const entry = memoryCache.get(cacheKey);
    if (entry && Date.now() - entry.time < CACHE_TTL_MS) {
      return entry.data;
    }
  }

  const headers = { 'Content-Type': 'application/json', ...options.headers };

  if (endpoint === '/auth/session') {
    const idToken = await getFirebaseIdToken(version);
    if (version !== sessionVersion) throw sessionExpiredError();
    if (!idToken) throw new Error('Not authenticated');
    const res = await fetch(`${API_BASE}/auth/session`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ id_token: idToken }),
      credentials: 'include',
    });
    if (!res.ok) {
      if (res.status === 401) return expireSession(version);
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Auth failed');
    }
    const data = await res.json();
    if (version !== sessionVersion) throw sessionExpiredError();
    accessToken = data.access_token;
    sessionExpired = false;
    clearApiCache();
    return data;
  }

  // Attach access token if present
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  let res;
  try {
    res = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });
  } catch (netErr) {
    // If request to remote API base failed and running on website, attempt colocated /api fallback
    if (API_BASE.startsWith('http') && typeof window !== 'undefined' && window.location.origin) {
      try {
        res = await fetch(`/api${endpoint}`, {
          ...options,
          headers,
          credentials: 'include',
        });
      } catch {
        throw netErr;
      }
    } else {
      throw netErr;
    }
  }

  // If 404 returned on remote API host, try colocated /api endpoint fallback once
  if (res.status === 404 && API_BASE.includes('api.zimetafsiriwa.site') && typeof window !== 'undefined') {
    try {
      const fallbackRes = await fetch(`/api${endpoint}`, {
        ...options,
        headers,
        credentials: 'include',
      });
      if (fallbackRes.ok) {
        res = fallbackRes;
      }
    } catch {
      // Keep original response
    }
  }

  // If 401 Unauthorized, attempt a silent token refresh and retry once
  if (res.status === 401 && endpoint !== '/auth/logout') {
    if (version !== sessionVersion) throw sessionExpiredError();
    const refreshedToken = await refreshAccessToken();
    if (version !== sessionVersion) throw sessionExpiredError();
    if (refreshedToken) {
      headers['Authorization'] = `Bearer ${refreshedToken}`;
      res = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers,
        credentials: 'include',
      });
    }
    if (res.status === 401) return expireSession(version);
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.message || `Request failed: ${res.status}`);
    error.status = res.status;
    throw error;
  }

  const data = await res.json();

  if (useCache && method === 'GET' && version === sessionVersion) {
    memoryCache.set(cacheKey, { data, time: Date.now() });
  }

  // Clear cache on write operations
  if (method !== 'GET') {
    clearApiCache();
  }

  return data;
}

// Videos are uploaded in small slices so no single request approaches the
// PHP / web-server / proxy body-size limits that make big uploads fail.
const VIDEO_CHUNK_SIZE = 2 * 1024 * 1024; // 2 MB stays below restrictive production proxy limits
const VIDEO_CHUNK_RETRIES = 3;
const VIDEO_CHUNK_CONCURRENCY = 3;
const VIDEO_SESSION_TTL = 7 * 24 * 60 * 60 * 1000;

async function videoFingerprint(file) {
  if (typeof crypto === 'undefined' || !crypto.subtle) return null;
  // Sample a bounded amount of data; never load a 1 GB video into browser memory.
  const sampleSize = 64 * 1024;
  const starts = [0, Math.max(0, Math.floor(file.size / 2) - sampleSize / 2), Math.max(0, file.size - sampleSize)];
  const samples = await Promise.all(starts.map(start => file.slice(start, Math.min(file.size, start + sampleSize)).arrayBuffer()));
  const identity = new Blob([JSON.stringify([file.name, file.size, file.lastModified]), ...samples]);
  const hash = await crypto.subtle.digest('SHA-256', await identity.arrayBuffer());
  return Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, '0')).join('');
}

function readUploadSession(key) {
  try {
    const session = JSON.parse(localStorage.getItem(key));
    if (session && /^[A-Za-z0-9-]{8,64}$/.test(session.id) &&
        Date.now() - session.updated < VIDEO_SESSION_TTL) return session;
  } catch { /* storage is optional in private/restricted browsers */ }
  return null;
}

function saveUploadSession(key, session) {
  if (!key) return;
  try {
    if (session) localStorage.setItem(key, JSON.stringify({ ...session, updated: Date.now() }));
    else localStorage.removeItem(key);
  } catch { /* uploads still work without persistent browser storage */ }
}

async function getVideoUploadStatus(uploadId, refreshed = false) {
  const version = sessionVersion;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 20000);
  try {
    const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    const response = await fetch(`${API_BASE}/uploads/video/status`, {
      method: 'POST', credentials: 'include', headers,
      body: JSON.stringify({ upload_id: uploadId }), signal: controller.signal,
    });
    if (response.status === 404 || response.status === 405) return null;
    if (response.status === 401 && version !== sessionVersion) throw sessionExpiredError();
    if (response.status === 401 && !refreshed && await refreshAccessToken()) {
      if (version !== sessionVersion) throw sessionExpiredError();
      return getVideoUploadStatus(uploadId, true);
    }
    if (response.status === 401) return expireSession(version);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || `Cannot check upload: ${response.status}`);
    return data;
  } finally {
    clearTimeout(timer);
  }
}

function sendUploadChunk(formData, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}/uploads/video/chunk`);
    xhr.withCredentials = true;
    xhr.timeout = 5 * 60 * 1000;
    xhr.setRequestHeader('Accept', 'application/json');
    if (accessToken) xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    xhr.onload = () => {
      let data = {};
      try { data = JSON.parse(xhr.responseText); } catch { /* proxy error page */ }
      if (xhr.status >= 200 && xhr.status < 300) return resolve(data);
      const error = new Error(data.message || `Upload failed: ${xhr.status}`);
      error.status = xhr.status;
      error.retryAfter = Number(xhr.getResponseHeader('Retry-After')) || 0;
      reject(error);
    };
    xhr.onerror = () => reject(new Error('Upload connection lost. Please check your internet connection.'));
    xhr.ontimeout = () => reject(new Error('Upload timed out. Please try again.'));
    xhr.onabort = () => reject(new Error('Upload cancelled.'));
    xhr.send(formData);
  });
}

function randomUploadId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

async function uploadOneChunkWithRetries({ uploadId, index, total, chunk, folder, extension, fileSize, chunkSize, fingerprint, onProgress }) {
  const version = sessionVersion;
  for (let attempt = 1; attempt <= VIDEO_CHUNK_RETRIES; attempt++) {
    const formData = new FormData();
    formData.append('upload_id', uploadId);
    formData.append('chunk_index', String(index));
    formData.append('index', String(index));
    formData.append('total_chunks', String(total));
    formData.append('total', String(total));
    formData.append('extension', extension || 'mp4');
    formData.append('folder', folder || 'videos');
    formData.append('file_size', String(fileSize));
    formData.append('chunk_size', String(chunkSize));
    if (fingerprint) formData.append('fingerprint', fingerprint);
    formData.append('chunk', chunk, 'chunk.bin');

    try {
      let result;
      try {
        result = await sendUploadChunk(formData, onProgress);
      } catch (error) {
        if (error.status === 401 && version !== sessionVersion) throw sessionExpiredError();
        if (error.status !== 401 || !(await refreshAccessToken())) throw error;
        if (version !== sessionVersion) throw sessionExpiredError();
        result = await sendUploadChunk(formData, onProgress);
      }
      onProgress(1);
      return result;
    } catch (err) {
      if (err.status === 401) return expireSession(version);
      const retryable = !err.status || [408, 429, 500, 502, 503, 504].includes(err.status);
      if (!retryable || attempt === VIDEO_CHUNK_RETRIES) throw err;
      onProgress(0);
      const delay = Math.max(err.retryAfter * 1000 || 0, 1000 * (2 ** (attempt - 1)) + Math.random() * 500);
      await new Promise(r => setTimeout(r, delay));
    }
  }
}

async function uploadVideoChunked(file, folder = 'videos', onProgress) {
  const extension = (file.name.split('.').pop() || 'mp4').toLowerCase().replace(/[^a-z0-9]/g, '') || 'mp4';

  if (!file.size) throw new Error('The selected video is empty.');
  if (!['mp4', 'webm', 'mov', 'mkv'].includes(extension)) throw new Error('Choose an MP4, WebM, MOV or MKV video.');

  const fingerprint = await videoFingerprint(file);
  const sessionKey = fingerprint ? `video-upload:${API_BASE}:${folder}:${fingerprint}` : null;
  let session = sessionKey && readUploadSession(sessionKey);
  let uploadId = session ? session.id : randomUploadId();
  const status = await getVideoUploadStatus(uploadId);
  let chunkSize = VIDEO_CHUNK_SIZE;
  let received = new Set();
  if (status) {
    if (!Number.isInteger(status.max_chunk_size) || status.max_chunk_size < 256 * 1024) {
      throw new Error('The server upload limit is too small. Please contact the administrator.');
    }
    chunkSize = Math.min(4 * 1024 * 1024, status.max_chunk_size);
    const meta = status.metadata;
    if (session && meta && meta.fingerprint === fingerprint && meta.file_size === file.size &&
        meta.extension === extension && Number.isInteger(meta.chunk_size) &&
        meta.chunk_size >= 256 * 1024 && meta.chunk_size <= status.max_chunk_size &&
        meta.total === Math.ceil(file.size / meta.chunk_size)) {
      chunkSize = meta.chunk_size;
      if (status.status === 'completed' && status.url) {
        saveUploadSession(sessionKey, null);
        clearApiCache();
        if (onProgress) onProgress(100);
        return status;
      }
      received = new Set((status.received || []).filter(i => Number.isInteger(i) && i >= 0 && i < meta.total));
    } else if (meta) {
      uploadId = randomUploadId();
    }
    session = { id: uploadId };
    saveUploadSession(sessionKey, session);
  } else {
    // A rolling deployment may still have the old chunk endpoint without status.
    saveUploadSession(sessionKey, null);
    uploadId = randomUploadId();
  }
  const totalChunks = Math.ceil(file.size / chunkSize);
  if (totalChunks > 20000 || file.size > 20 * 1024 ** 3) throw new Error('This video exceeds the supported upload size.');
  const uploadedBytes = new Array(totalChunks).fill(0);
  let transferred = 0;
  let nextChunk = 0;
  let failure = null;
  let lastPercent = -1;
  const reportProgress = () => {
    const percent = Math.min(99, Math.round(transferred / file.size * 100));
    if (percent !== lastPercent && onProgress) onProgress(percent);
    lastPercent = percent;
  };
  for (const index of received) {
    uploadedBytes[index] = Math.min(chunkSize, file.size - index * chunkSize);
    transferred += uploadedBytes[index];
  }
  reportProgress();

  const sendChunk = async (i) => {
    const start = i * chunkSize;
    const end = Math.min(file.size, start + chunkSize);
    const chunkBlob = file.slice(start, end);

    return uploadOneChunkWithRetries({
      uploadId,
      index: i,
      total: totalChunks,
      chunk: chunkBlob,
      folder,
      extension,
      fileSize: file.size,
      chunkSize,
      fingerprint,
      onProgress: (fraction) => {
        const bytes = chunkBlob.size * fraction;
        transferred += bytes - uploadedBytes[i];
        uploadedBytes[i] = bytes;
        reportProgress();
      },
    });
  };

  // Reserve the final chunk until all others are acknowledged. This also works
  // with older servers that assemble as soon as the final index arrives.
  const worker = async () => {
    while (!failure && nextChunk < totalChunks - 1) {
      const index = nextChunk++;
      if (received.has(index)) continue;
      try { await sendChunk(index); } catch (error) { failure = error; }
    }
  };
  await Promise.all(Array.from({ length: Math.min(VIDEO_CHUNK_CONCURRENCY, totalChunks - 1) }, worker));
  if (failure) throw failure;

  const result = await sendChunk(totalChunks - 1);
  // Accept the URL from older deployments that omit the completed status.
  if (result && result.url) {
    saveUploadSession(sessionKey, null);
    if (onProgress) onProgress(100);
    clearApiCache();
    return result;
  }

  throw new Error('Video upload completed without returning a storage URL');
}

// Uses XMLHttpRequest (not fetch) because only XHR exposes upload progress events.
function uploadWithProgress(endpoint, file, folder, onProgress, refreshed = false) {
  const version = sessionVersion;
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}${endpoint}`);
    xhr.withCredentials = true;
    if (accessToken) xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);

    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = async () => {
      let data = {};
      try { data = JSON.parse(xhr.responseText); } catch { /* non-JSON error body */ }
      if (xhr.status >= 200 && xhr.status < 300) {
        clearApiCache();
        resolve(data);
      } else {
        if (xhr.status === 401) {
          try {
            if (version !== sessionVersion) throw sessionExpiredError();
            if (!refreshed) {
              await refreshAccessToken();
              if (version !== sessionVersion) throw sessionExpiredError();
              resolve(await uploadWithProgress(endpoint, file, folder, onProgress, true));
            } else {
              await expireSession(version);
            }
          } catch (error) { reject(error); }
          return;
        }
        reject(new Error(data.message || `Upload failed: ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error('Upload failed'));

    xhr.send(formData);
  });
}

export const api = {
  get: (url) => request(url),
  post: (url, data) => request(url, { method: 'POST', body: JSON.stringify(data) }),
  put: (url, data) => request(url, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (url) => request(url, { method: 'DELETE' }),
  login: () => request('/auth/session'),
  logout: async () => {
    const logoutRequest = request('/auth/logout', { method: 'POST' });
    clearApiSession();
    return logoutRequest;
  },
  getVideos: async (fresh = false) => {
    const res = await request('/videos', {}, !fresh);
    return Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
  },
  getVideo: async (id, fresh = false) => {
    const res = await request(`/videos/${id}`, {}, !fresh);
    return res?.data ?? res;
  },
  getTrending: async (fresh = false) => {
    const res = await request('/videos/trending', {}, !fresh);
    return Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
  },
  getPopular: async (fresh = false) => {
    const res = await request('/videos/popular', {}, !fresh);
    return Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
  },
  getTransactions: async () => {
    const res = await request('/transactions');
    return Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
  },
  streamVideo: (id) => request(`/videos/${id}/stream`, { method: 'POST' }),
  streamEpisode: (videoId, episodeId) => request(`/videos/${videoId}/episodes/${episodeId}/stream`, { method: 'POST' }),
  createPaymentOrder: (data) => request('/payments/sonicpesa/order', { method: 'POST', body: JSON.stringify(data) }),
  getPaymentStatus: (orderId) => request(`/payments/sonicpesa/orders/${orderId}`),
  getPaymentGateway: async () => {
    const res = await request('/payments/gateway');
    return res?.data ?? res;
  },
  uploadImage: (file, folder = 'thumbnails', onProgress) => uploadWithProgress('/uploads/image', file, folder, onProgress),
  uploadVideo: (file, folder = 'videos', onProgress) => uploadVideoChunked(file, folder, onProgress),
  clearCache: clearApiCache,
};

export { normalizeMediaUrl, DEFAULT_POSTER_SVG } from './utils/media';
